import React, { useState } from "react";
import {
  TextInput,
  Textarea,
  SimpleGrid,
  Text,
  Box,
  Stack,
  Button,
  Image,
  ActionIcon,
  Paper,
} from "@mantine/core";
import { IconUpload, IconTrash } from "@tabler/icons-react";
import { API_BASE_URL } from "../../../config/baseUrls";
import { uploadFetch } from "../../../utils/directUpload";

// Sleek, modern uploader with explicit dimension instructions
const LocalImageUploader = ({
  label,
  description,
  dimensions,
  value,
  onUploadChange,
}) => {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        const res = await uploadFetch(
          `${API_BASE_URL}/auth/uploadImage`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("shopToken")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ image: base64, name: file.name }),
          },
        );

        const data = await res.json();
        if (data.url) {
          onUploadChange(data.url);
        } else {
          alert("Upload failed. Please try again.");
        }
      } catch (err) {
        console.error("Upload error:", err);
        alert("Network error during upload.");
      } finally {
        setUploading(false);
      }
    };
  };

  return (
    <Box>
      <Text size="lg" fw={600} mb={2}>
        {label}
      </Text>
      {/* Explicit highly-visible dimension requirement */}
      {dimensions && (
        <Text size="xs" fw={800} c="blue.6" tt="uppercase" mb={4}>
          {dimensions}
        </Text>
      )}
      <Text size="sm" c="dimmed" mb="sm">
        {description}
      </Text>

      {value ? (
        <Box
          pos="relative"
          w="100%"
          h={180}
          bg="gray.0"
          style={{
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid var(--mantine-color-gray-3)",
          }}
        >
          <Image src={value} h="100%" w="100%" fit="cover" />
          <ActionIcon
            color="red"
            variant="filled"
            size="lg"
            pos="absolute"
            bottom={10}
            right={10}
            onClick={() => onUploadChange("")}
          >
            <IconTrash size={20} />
          </ActionIcon>
        </Box>
      ) : (
        <Button
          component="label"
          variant="light"
          color="blue"
          fullWidth
          h={180}
          style={{
            border: "2px dashed var(--mantine-color-blue-3)",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "var(--mantine-color-blue-0)",
          }}
          loading={uploading}
        >
          {!uploading && (
            <Stack gap={8} align="center">
              <IconUpload size={32} color="var(--mantine-color-blue-6)" />
              <Text size="md" fw={600} c="blue.7">
                Click to browse files
              </Text>
            </Stack>
          )}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleFileChange}
          />
        </Button>
      )}
    </Box>
  );
};

export default function StallDetailsStep({ formData, setFormData }) {
  return (
    <Stack gap="xl">
      <Paper
        withBorder
        p="md"
        radius="md"
        bg="gray.0"
        style={{ borderColor: "var(--mantine-color-gray-3)" }}
      >
        <Text size="lg" fw={700} mb="xs">
          Store Identity
        </Text>
        <Text size="md" c="dimmed" mb="lg">
          This is what customers will see when they visit your store or scan
          your QR code.
        </Text>

        <Stack gap="md">
          <TextInput
            label="Stall / Brand Name"
            size="lg"
            placeholder="Example: Anime Merch Store or Big Burger Co."
            description="Type the exact name you want displayed at the top of your menu."
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <Textarea
            label="Store Description"
            size="lg"
            placeholder="Example: We sell the finest custom anime apparel and accessories..."
            description="Write a short bio telling customers what makes your store special."
            minRows={4}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </Stack>
      </Paper>

      <Paper
        withBorder
        p="md"
        radius="md"
        bg="gray.0"
        style={{ borderColor: "var(--mantine-color-gray-3)" }}
      >
        <Text size="lg" fw={700} mb="xs">
          Social Media (Optional)
        </Text>
        <Text size="md" c="dimmed" mb="lg">
          Link your socials so customers can follow you after they purchase.
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <TextInput
            label="Instagram Handle"
            size="lg"
            placeholder="Example: @yourbrandname"
            value={formData.socialLinks.instagram}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  instagram: e.target.value,
                },
              })
            }
          />
          <TextInput
            label="Website URL"
            size="lg"
            placeholder="Example: https://yourshop.com"
            value={formData.socialLinks.website}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  website: e.target.value,
                },
              })
            }
          />
        </SimpleGrid>
      </Paper>

      <Paper
        withBorder
        p="md"
        radius="md"
        bg="gray.0"
        style={{ borderColor: "var(--mantine-color-gray-3)" }}
      >
        <Text size="lg" fw={700} mb="lg">
          Store Graphics
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
          <LocalImageUploader
            label="Brand Logo"
            dimensions="Recommended: 500 x 500 pixels (Square)"
            description="This will be the profile picture for your store."
            value={formData.logoUrl}
            onUploadChange={(url) => setFormData({ ...formData, logoUrl: url })}
          />
          <LocalImageUploader
            label="Banner Image"
            dimensions="Recommended: 1200 x 400 pixels (Wide)"
            description="This appears at the very top of your store page."
            value={formData.bannerImageUrl}
            onUploadChange={(url) =>
              setFormData({ ...formData, bannerImageUrl: url })
            }
          />
        </SimpleGrid>
      </Paper>
    </Stack>
  );
}
